import { signal } from '@preact/signals';
import type { PublisherChapter, PublisherManifest } from '@fcbh/schema';

/**
 * Publisher mode — the Reader's adapter for partner-PWA deploys baked by
 * Geauxjüre. On boot, fetch `/data/publisher.json`. If present, the Reader
 * runs entirely off the local `/data/*.json` files and never calls Bible
 * Brain at runtime. If absent, the Reader behaves exactly as it did before
 * (ESV on fcbh-reader.vercel.app, hits BB directly).
 *
 * One signal, two helpers. Nothing else in the Reader needs to know how
 * data gets in.
 */

export const publisher = signal<PublisherManifest | null>(null);

let bootPromise: Promise<PublisherManifest | null> | null = null;

/**
 * Lazy boot — fires once. Subsequent calls return the same promise.
 * Resolved value also lands in the `publisher` signal so reactive consumers
 * (Verse, ChapterPicker, etc.) can subscribe.
 */
export function bootPublisherMode(): Promise<PublisherManifest | null> {
  if (bootPromise) return bootPromise;
  bootPromise = (async (): Promise<PublisherManifest | null> => {
    try {
      const res = await fetch('/data/publisher.json', { cache: 'force-cache' });
      if (!res.ok) return null;
      const data = (await res.json()) as PublisherManifest;
      if (data && data.kind === 'publisher' && Array.isArray(data.books)) {
        publisher.value = data;
        return data;
      }
      return null;
    } catch {
      return null;
    }
  })();
  return bootPromise;
}

const chapterCache = new Map<string, PublisherChapter>();

/**
 * Fetch a baked chapter from the publisher deploy. Cached in-memory.
 * Returns null on 404/parse-error so callers can fall through to whatever
 * empty state they want to render.
 */
export async function loadPublisherChapter(
  bookId: string,
  chapter: number,
): Promise<PublisherChapter | null> {
  const key = `${bookId}-${chapter}`;
  const hit = chapterCache.get(key);
  if (hit) return hit;
  try {
    const res = await fetch(`/data/${bookId}-${chapter}.json`, { cache: 'force-cache' });
    if (!res.ok) return null;
    const data = (await res.json()) as PublisherChapter;
    chapterCache.set(key, data);
    return data;
  } catch {
    return null;
  }
}

/** True iff the Reader is running as a partner deploy. */
export function isPublisherMode(): boolean {
  return publisher.value !== null;
}
